'use client';

import * as React from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Calendar, Calculator } from 'lucide-react';

interface LinkPreviewProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
	url: string;
	children: React.ReactNode;
}

export function LinkPreview({ url, children, className, ...props }: LinkPreviewProps) {
	const [isHovered, setIsHovered] = React.useState(false);
	const [previewData, setPreviewData] = React.useState<{
		title: string;
		description: string;
		icon: React.ElementType;
	} | null>(null);

	React.useEffect(() => {
		if (isHovered) {
			// Set preview data based on the URL
			if (url === '/contact') {
				setPreviewData({
					title: 'Book a Discovery Session',
					description:
						'Schedule a free consultation to discuss your business needs and explore how we can help.',
					icon: Calendar,
				});
			} else if (url === '/calculator') {
				setPreviewData({
					title: 'Project Calculator',
					description:
						'Get an instant estimate for your project using our interactive cost calculator.',
					icon: Calculator,
				});
			}
		}
	}, [isHovered, url]);

	return (
		<span className="relative inline-block">
			<a
				href={url}
				className={cn('text-primary hover:text-primary/90 transition-colors', className)}
				onMouseEnter={() => setIsHovered(true)}
				onMouseLeave={() => setIsHovered(false)}
				{...props}
			>
				{children}
			</a>
			{isHovered && previewData && (
				<motion.div
					initial={{ opacity: 0, y: 10 }}
					animate={{ opacity: 1, y: 0 }}
					exit={{ opacity: 0, y: 10 }}
					className="absolute left-1/2 -translate-x-1/2 mt-2 w-72 bg-background/95 backdrop-blur-sm border rounded-lg shadow-lg overflow-hidden z-50"
				>
					<div className="p-4 flex items-start gap-3">
						<div className="p-2 rounded-md bg-primary/10">
							<previewData.icon className="w-5 h-5 text-primary" />
						</div>
						<div>
							<h3 className="font-medium text-sm text-foreground">{previewData.title}</h3>
							<p className="text-xs text-muted-foreground mt-1">{previewData.description}</p>
						</div>
					</div>
				</motion.div>
			)}
		</span>
	);
}
